import { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import Select from "react-select";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import { Icon } from "@mui/material";
import moment from "moment";
import { updateUserData } from "../../app/auth/authSlice";
import { appSettings } from "../../app/appSettingsSlice";
import { getCurrencySymbol } from "../../utils/currencyDisplay";
import "./profileAccount.css";

const isHttpUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

const urlFields = new Set([
  "CompWebsite",
  "CompFacebooklink",
  "CompInstagramLink",
  "CompYoutubeLink",
  "CompTikTokLink",
  "CompGoogleReview",
]);

/** True when a pending email change exists and has not yet expired (UTC). */
const isPendingEmailChangeActive = (pendingEmail, expiresUtc) => {
  if (!pendingEmail) return false;
  if (!expiresUtc) return true;
  return moment.utc(expiresUtc).isAfter(moment.utc());
};

const pickSettingsString = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const mapApiToState = (apiData, settingsData, baseURL = "") => {
  // Helper function to construct full URL from relative path
  const constructImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (typeof imagePath !== "string") return "";
    // If already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    // If no baseURL provided, return the path as is (will be handled elsewhere)
    if (!baseURL) return imagePath;
    // Construct full URL
    const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
    const pathClean = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
    return `${baseUrlClean}${pathClean}`;
  };

  return {
    CompID: apiData.compID || 0,
    CompName: apiData.compName || "",
    CompDetail: apiData.compDetail || "",
    CompPhone: apiData.compPhone || "",
    CompEmail: apiData.compEmail || "",
    CompCountry: apiData.compCountry || "UK",
    CompCity: apiData.compCity || "",
    CompLanguage: apiData.compLanguage || "",
    CompCurrency: apiData.compCurrency || "",
    CompTimeZone: apiData.compTimeZone || "",
    CompUTCTimeZone: apiData.compUTCTimeZone || "",
    CompAddress: apiData.compAddress || "",
    CompWebsite: apiData.compWebsite || "",
    CompFax: apiData.compFax || "",
    CompIndustryType: apiData.compIndustryType || "",
    AndroidAppver: apiData.androidAppver || "",
    IOSAppver: apiData.iosAppver || "",
    CompSecCode: apiData.compSecCode || "",
    CompLatitude: apiData.compLatitude || 0,
    CompLongitude: apiData.compLongitude || 0,
    CompIcon: constructImageUrl(apiData.compIcon || ""),
    CompCoverImage: constructImageUrl(apiData.compCoverImage || ""),
    MinOrderAmount: settingsData?.s_MinOrderAmount || 0,
    UpdateProCPriceAgainstMasterProductCPriceUpdate:
      apiData.updateProCPriceAgainstMasterProductCPriceUpdate || false,
    CompPNotificationLimit_PerDay: apiData.compPNotificationLimit_PerDay || 0,
    CompMonthlySmsLimit: apiData.compMonthlySmsLimit ?? 0,
    CompMaxIPadLicense: apiData.compMaxIPadLicense || 0,
    CompMaxUserLicense: apiData.compMaxUserLicense || 0,
    WaiterAppMaxDevices: apiData.waiterAppMaxDevices || 0,
    CompRegDate: apiData.compRegDate || "",
    CompExpireDate: apiData.compExpireDate || "",
    LocationLicences: apiData.locationLicences || 0,
    MobileAppExpireDate: apiData.mobileAppExpireDate || "",
    WebAppExpireDate: apiData.webAppExpireDate || "",
    WaiterAppExpireDate: apiData.waiterAppExpireDate || "",
    IsKitchenApp: apiData.isKitchenApp || false,
    ISMobileApp: apiData.isMobileApp || false,
    IsTVApp: apiData.isTVApp || false,
    ISWebApp: apiData.isWebApp || false,
    IsWaiterApp: apiData.isWaiterApp || false,
    CompPublish: apiData.compPublish || false,
    CompRecoveryEmail: apiData.compRecoveryEmail || "",
    CompRecoveryPhoneNumber: apiData.compRecoveryPhoneNumber || "",
    // Security contacts — company profile (CompRecovery*)
    // Online ordering contacts — company settings (S_OnlineOrdering*)
    S_OnlineOrderingEmail: pickSettingsString(
      settingsData?.s_OnlineOrderingEmail,
      settingsData?.S_OnlineOrderingEmail,
      settingsData?.onlineOrderingEmail
    ),
    S_OnlineOrderingPhone: pickSettingsString(
      settingsData?.s_OnlineOrderingPhone,
      settingsData?.S_OnlineOrderingPhone,
      settingsData?.onlineOrderingPhone
    ),
    CompFacebooklink: apiData.compFacebooklink || "",
    CompInstagramLink: apiData.compInstagramLink || "",
    CompYoutubeLink: apiData.compYoutubeLink || "",
    CompTikTokLink: apiData.compTikTokLink || "",
    CompGoogleReview: apiData.compGoogleReview || "",
    CompanySlug: apiData.companySlug || "",
    PendingCompEmail: apiData.pendingCompEmail || "",
    CompEmailChangeExpiresUtc: apiData.compEmailChangeExpiresUtc || null,
  };
};
function Profile() {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData, settingsData } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());
  const logoOpenLibraryRef = useRef(null);
  const [isGeneralEditing, setIsGeneralEditing] = useState(false);
  const [generalPreviousData, setGeneralPreviousData] = useState(null);
  const [isLogoEditing, setIsLogoEditing] = useState(false);
  const [logoPreviousData, setLogoPreviousData] = useState(null);
  const [isCoverImageEditing, setIsCoverImageEditing] = useState(false);
  const [coverImagePreviousData, setCoverImagePreviousData] = useState(null);
  const [selectedImageType, setSelectedImageType] = useState("logo"); // "logo" or "coverImage"
  const [isImageEditing, setIsImageEditing] = useState(false);
  const [imagePreviousData, setImagePreviousData] = useState(null);
  const [isSocialLinksEditing, setIsSocialLinksEditing] = useState(false);
  const [socialLinksPreviousData, setSocialLinksPreviousData] = useState(null);
  const [isBusinessDetailsEditing, setIsBusinessDetailsEditing] =
    useState(false);
  const [businessDetailsPreviousData, setBusinessDetailsPreviousData] =
    useState(null);
  const [isSecurityEditing, setIsSecurityEditing] = useState(false);
  const [securityPreviousData, setSecurityPreviousData] = useState(null);
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings || {};
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const profileThemeStyle = {
    "--profile-accent": themeColor,
    "--profile-accent-soft": `${themeColor}24`,
    "--profile-accent-shadow": `${themeColor}40`,
  };

  const renderSectionHeading = (title, subtitle) => (
    <div className="profile-section-heading">
      <div className="d-flex align-items-start gap-2">
        <span className="profile-section-heading__bar" aria-hidden />
        <div className="min-w-0">
          <h2 className="profile-section-heading__title h6">{title}</h2>
          {subtitle ? (
            <p className="profile-section-heading__subtitle">{subtitle}</p>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderFieldValue = (displayValue, { multiline = false, href = null } = {}) => {
    const empty =
      displayValue === "" ||
      displayValue === null ||
      displayValue === undefined;
    if (empty) {
      return (
        <span className="profile-field__value profile-field__value--empty">
          Not set
        </span>
      );
    }
    if (href) {
      return (
        <span className="profile-field__value">
          <a href={href} target="_blank" rel="noopener noreferrer">
            {displayValue}
          </a>
        </span>
      );
    }
    return (
      <span
        className={`profile-field__value${
          multiline ? " profile-field__value--multiline" : ""
        }`}
      >
        {displayValue}
      </span>
    );
  };

  const [companyData, setCompanyData] = useState({
    CompID: CompID || 0,
    CompName: "",
    CompDetail: "",
    CompPhone: "",
    CompEmail: "",
    CompCountry: "UK",
    CompCity: "",
    CompLanguage: "",
    CompCurrency: "",
    CompTimeZone: "",
    CompUTCTimeZone: "",
    CompAddress: "",
    CompWebsite: "",
    CompFax: "",
    CompIndustryType: "",
    AndroidAppver: "",
    IOSAppver: "",
    CompSecCode: "",
    CompLatitude: 0,
    CompLongitude: 0,
    CompIcon: "",
    CompCoverImage: "",
    MinOrderAmount: 0,
    UpdateProCPriceAgainstMasterProductCPriceUpdate: false,
    CompPNotificationLimit_PerDay: 0,
    CompMonthlySmsLimit: 0,
    CompMaxIPadLicense: 0,
    CompMaxUserLicense: 0,
    WaiterAppMaxDevices: 0,
    CompRegDate: "",
    CompExpireDate: "",
    LocationLicences: 0,
    MobileAppExpireDate: "",
    WebAppExpireDate: "",
    WaiterAppExpireDate: "",
    IsKitchenApp: false,
    ISMobileApp: false,
    IsTVApp: false,
    ISWebApp: false,
    IsWaiterApp: false,
    CompPublish: false,
    CompRecoveryEmail: "",
    CompRecoveryPhoneNumber: "",
    S_OnlineOrderingEmail: "",
    S_OnlineOrderingPhone: "",
    CompFacebooklink: "",
    CompInstagramLink: "",
    CompYoutubeLink: "",
    CompTikTokLink: "",
    CompGoogleReview: "",
    CompanySlug: "",
    PendingCompEmail: "",
    CompEmailChangeExpiresUtc: null,
  });
  const [isCompanyEmailEditingInline, setIsCompanyEmailEditingInline] =
    useState(false);
  const [emailChangeNew, setEmailChangeNew] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [timeZones, setTimeZones] = useState([]);
  const [languages, setLanguages] = useState([
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" },
    { value: "it", label: "Italian" },
  ]);
  const [selectedCity, setSelectedCity] = useState(null);
  /** country Code / Code2 → CallCode from Country list */
  const [countryCallCodeMap, setCountryCallCodeMap] = useState({});
  const [logoState, setLogoState] = useState({
    CompIcon: "",
    logoFile: null,
  });
  const [coverImageState, setCoverImageState] = useState({
    CompCoverImage: "",
    coverImageFile: null,
  });

  const formatDialCode = useCallback((raw) => {
    if (!raw || typeof raw !== "string") return "";
    const trimmed = raw.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
  }, []);

  const companyDialCode = formatDialCode(
    countryCallCodeMap[companyData.CompCountry] ||
      countryCallCodeMap[(companyData.CompCountry || "").toUpperCase()] ||
      ""
  );

  const fetchCountryCallCodes = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COUNTRIES);
      const list = response.data?.data || response.data || [];
      const map = {};
      (Array.isArray(list) ? list : []).forEach((country) => {
        const callCode = country.callCode ?? country.CallCode ?? "";
        const code = country.code ?? country.Code ?? "";
        const code2 = country.code2 ?? country.Code2 ?? "";
        if (code) map[code] = callCode;
        if (code2) map[code2] = callCode;
      });
      setCountryCallCodeMap(map);
    } catch {
      // Non-blocking — phone fields still work without dial prefix
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CURRENCIES);
      setCurrencies(response.data.data);
    } catch (err) {
      showErrorToast("Failed to load currencies");
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchTimeZones = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_TIMEZONE);
      setTimeZones(response.data.data);
    } catch (err) {
      showErrorToast("Failed to load time zones");
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchCompanyData = useCallback(
    async (setLoadingState = true) => {
      try {
        if (setLoadingState) {
          setLoading(true);
        }
        // Fetch fresh data from APIs
        const [profileResponse, settingsResponse] = await Promise.all([
          axiosInstance.get(APIs.GET_COMPANY_PROFILE),
          axiosInstance.get(APIs.GET_COMPANY_SETTINGS),
        ]);
        console.log(profileResponse.data.data);
        const freshProfileData = profileResponse.data.data;
        const freshSettingsData = settingsResponse.data.data;
        // Update Redux state with fresh data
        dispatch(
          updateUserData({
            profileData: freshProfileData,
            settingsData: freshSettingsData,
            lastDataRefresh: new Date().toISOString(),
          })
        );
        // Get baseURL from axiosInstance for constructing full image URLs
        const baseURL = axiosInstance.defaults.baseURL || "";
        const mappedData = mapApiToState(freshProfileData, freshSettingsData, baseURL);
        setCompanyData(mappedData);
        setLogoState({
          CompIcon: mappedData.CompIcon || undefined,
          logoFile: null,
        });
        setCoverImageState({
          CompCoverImage: mappedData.CompCoverImage || undefined,
          coverImageFile: null,
        });
        setImageKey(Date.now());
      } catch (err) {
        showErrorToast("Failed to load Company Data");
      } finally {
        if (setLoadingState) {
          setLoading(false);
        }
      }
    },
    [dispatch]
  );
  useEffect(() => {
    fetchCurrencies();
    fetchTimeZones();
    fetchCountryCallCodes();
    fetchCompanyData();
  }, []);

  const handleSelectChange = (name, option) => {
    setEditValue(option ? option.value : "");
  };
  const handleTimeZoneSelectChange = (name, option) => {
    setEditValue(option || "");
  };
  const handleToggleChange = (name, checked) => {
    setEditValue(checked);
  };
  const startLogoEditing = () => {
    setIsLogoEditing(true);
    setLogoPreviousData({
      CompIcon: companyData.CompIcon,
    });
  };
  const startCoverImageEditing = () => {
    setIsCoverImageEditing(true);
    setCoverImagePreviousData({
      CompCoverImage: companyData.CompCoverImage,
    });
  };
  const startImageEditing = () => {
    setIsImageEditing(true);
    setImagePreviousData({
      CompIcon: companyData.CompIcon,
      CompCoverImage: companyData.CompCoverImage,
    });
  };
  const cancelLogoEditing = () => {
    if (logoPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...logoPreviousData }));
      setLogoState({
        CompIcon: logoPreviousData.CompIcon || "",
        logoFile: null,
      });
    }
    setIsLogoEditing(false);
    setLogoPreviousData(null);
  };
  const cancelCoverImageEditing = () => {
    if (coverImagePreviousData) {
      setCompanyData((prev) => ({ ...prev, ...coverImagePreviousData }));
      setCoverImageState({
        CompCoverImage: coverImagePreviousData.CompCoverImage || "",
        coverImageFile: null,
      });
    }
    setIsCoverImageEditing(false);
    setCoverImagePreviousData(null);
  };
  const cancelImageEditing = () => {
    if (imagePreviousData) {
      setCompanyData((prev) => ({ ...prev, ...imagePreviousData }));
      setLogoState({
        CompIcon: imagePreviousData.CompIcon || "",
        logoFile: null,
      });
      setCoverImageState({
        CompCoverImage: imagePreviousData.CompCoverImage || "",
        coverImageFile: null,
      });
    }
    setIsImageEditing(false);
    setImagePreviousData(null);
  };
 // Helper function to extract relative path from full URL
const extractRelativePath = (path) => {
  if (!path) return "";
  
  // If it's already a relative path (doesn't start with http), return as-is
  if (!path.startsWith("http://") && !path.startsWith("https://")) {
    return path;
  }
  
  // Extract relative path from full URL
  // Handle both forward and backslash paths
  if (path.includes("uploads/")) {
    return "uploads/" + path.split("uploads/").pop().replace(/\\/g, "/");
  }
  if (path.includes("uploads\\")) {
    return "uploads/" + path.split("uploads\\").pop().replace(/\\/g, "/");
  }
  
  // If no uploads folder found, try to extract path after domain
  try {
    const url = new URL(path);
    const pathname = url.pathname;
    // Remove leading slash and return
    return pathname.startsWith("/") ? pathname.substring(1) : pathname;
  } catch (e) {
    // If URL parsing fails, return empty string
    return "";
  }
};

const handleRemoveLogo = () => {
  setLogoState({ CompIcon: "", logoFile: null });
  // Force imageKey update to trigger ImageUpload re-render
  setImageKey(Date.now());
  // Don't update companyData here - it will be updated when save is called
};
const handleRemoveCoverImage = () => {
  setCoverImageState({ CompCoverImage: "", coverImageFile: null });
  // Force imageKey update to trigger ImageUpload re-render
  setImageKey(Date.now());
  // Don't update companyData here - it will be updated when save is called
};
const handleRemoveImage = () => {
  if (selectedImageType === "logo") {
    handleRemoveLogo();
  } else {
    handleRemoveCoverImage();
  }
};
  const startGeneralEditing = () => {
    setIsGeneralEditing(true);
    setGeneralPreviousData({
      CompName: companyData.CompName,
      CompDetail: companyData.CompDetail,
      CompPhone: companyData.CompPhone,
      CompEmail: companyData.CompEmail,
      CompCountry: companyData.CompCountry,
      CompCity: companyData.CompCity,
      CompLanguage: companyData.CompLanguage,
      CompCurrency: companyData.CompCurrency,
      CompTimeZone: companyData.CompTimeZone,
      CompAddress: companyData.CompAddress,
      CompFax: companyData.CompFax,
      CompanySlug: companyData.CompanySlug,
    });
  };
  const startSocialLinksEditing = () => {
    setIsSocialLinksEditing(true);
    setSocialLinksPreviousData({
      CompWebsite: companyData.CompWebsite,
      CompFacebooklink: companyData.CompFacebooklink,
      CompInstagramLink: companyData.CompInstagramLink,
      CompYoutubeLink: companyData.CompYoutubeLink,
      CompTikTokLink: companyData.CompTikTokLink,
      CompGoogleReview: companyData.CompGoogleReview,
    });
  };
  const startBusinessDetailsEditing = () => {
    setIsBusinessDetailsEditing(true);
    setBusinessDetailsPreviousData({
      CompIndustryType: companyData.CompIndustryType,
      CompSecCode: companyData.CompSecCode,
      CompLatitude: companyData.CompLatitude,
      CompLongitude: companyData.CompLongitude,
    });
  };
  const startSecurityEditing = () => {
    setIsSecurityEditing(true);
    setSecurityPreviousData({
      CompRecoveryEmail: companyData.CompRecoveryEmail,
      CompRecoveryPhoneNumber: companyData.CompRecoveryPhoneNumber,
      S_OnlineOrderingEmail: companyData.S_OnlineOrderingEmail,
      S_OnlineOrderingPhone: companyData.S_OnlineOrderingPhone,
    });
  };
  const cancelGeneralEditing = () => {
    if (generalPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...generalPreviousData }));
      setLogoState({
        CompIcon: generalPreviousData.CompIcon || "",
        logoFile: null,
      });
      setCoverImageState({
        CompCoverImage: generalPreviousData.CompCoverImage || "",
        coverImageFile: null,
      });
    }
    setIsGeneralEditing(false);
    setGeneralPreviousData(null);
  };
  const cancelSocialLinksEditing = () => {
    if (socialLinksPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...socialLinksPreviousData }));
    }
    setIsSocialLinksEditing(false);
    setSocialLinksPreviousData(null);
  };
  const cancelBusinessDetailsEditing = () => {
    if (businessDetailsPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...businessDetailsPreviousData }));
    }
    setIsBusinessDetailsEditing(false);
    setBusinessDetailsPreviousData(null);
  };
  const cancelSecurityEditing = () => {
    if (securityPreviousData) {
      setCompanyData((prev) => ({ ...prev, ...securityPreviousData }));
    }
    setIsSecurityEditing(false);
    setSecurityPreviousData(null);
  };
  const startEditing = (name, value) => {
    setEditingField(name);
    if (name === "CompCountryCity") {
      setEditValue({
        CompCountry: companyData.CompCountry,
        CompCity: companyData.CompCity,
      });
    } else if (name === "CompIcon") {
      setEditValue(companyData.CompIcon);
      setLogoState((prev) => ({
        ...prev,
        CompIcon: companyData.CompIcon || "",
      }));
    } else if (name === "CompCoverImage") {
      setEditValue(companyData.CompCoverImage);
      setCoverImageState((prev) => ({
        ...prev,
        CompCoverImage: companyData.CompCoverImage || "",
      }));
    } else {
      setEditValue(value);
    }
  };
  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
    setLogoState((prev) => ({
      ...prev,
      CompIcon: companyData.CompIcon || "",
      logoFile: null,
    }));
    setCoverImageState((prev) => ({
      ...prev,
      CompCoverImage: companyData.CompCoverImage || "",
      coverImageFile: null,
    }));
  };
  const cancelEditingAfterSubmit = () => {
    setEditingField(null);
    setEditValue("");
    setSelectedCity(null);
    setLogoState({ CompIcon: companyData.CompIcon || "", logoFile: null });
  };
const saveLogoSettings = async () => {
  try {
    setLoading(true);

    const hadExistingLogo = !!(companyData.CompIcon || logoPreviousData?.CompIcon);
    const isRemovingLogo = !logoState.CompIcon && !logoState.logoFile && hadExistingLogo;

    if (isRemovingLogo) {
      try {
        await axiosInstance.delete(APIs.REMOVE_COMPANY_LOGO);
        showSuccessToast("Logo removed successfully");
        setCompanyData((prev) => ({ ...prev, CompIcon: "" }));
        setLogoState({ CompIcon: "", logoFile: null });
        setImageKey(Date.now());
        await fetchCompanyData(false);
        setIsLogoEditing(false);
        setLogoPreviousData(null);
        setLoading(false);
        return;
      } catch (err) {
        showErrorToast("Failed to remove logo");
        setLoading(false);
        return;
      }
    }

    if (logoState.logoFile) {
      const logoFormData = new FormData();
      logoFormData.append("ImageFile", logoState.logoFile);
      const baseURL = axiosInstance.defaults.baseURL || "";
      try {
        const logoResponse = await axiosInstance.post(
          APIs.POST_UPDATE_COMPANY_LOGO,
          logoFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        const relativePath =
          logoResponse.data?.data?.filePath ||
          logoResponse.data?.data?.imageUrl ||
          logoResponse.data?.filePath ||
          logoResponse.data?.imageUrl ||
          logoResponse.data?.url;

        if (relativePath) {
          // Backend stores relative path (e.g., "uploads/filename.ext")
          // The service adds baseUrl when fetching, so we should store only the relative path
          // Construct full URL for display purposes only
          let fullImageUrl;
          if (relativePath.startsWith("http")) {
            fullImageUrl = relativePath;
          } else {
            const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
            const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
            fullImageUrl = `${baseUrlClean}${pathClean}`;
          }

          // Store only the relative path in database (backend service will add baseUrl when fetching)
          const updatedCompanyData = {
            ...companyData,
            // Keep image fields as relative paths in payload to avoid URL duplication issues.
            CompIcon: relativePath,
            CompCoverImage: extractRelativePath(companyData.CompCoverImage),
            CompLatitude: companyData.CompLatitude.toString(),
            CompLongitude: companyData.CompLongitude.toString(),
          };
          const response = await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);

          // Update local state with full URL for display (but DB has relative path)
          setCompanyData((prev) => ({ ...prev, CompIcon: fullImageUrl }));
          setLogoState({ CompIcon: fullImageUrl, logoFile: null });
          setImageKey(Date.now()); // Update imageKey to force refresh
          showSuccessToast(response.data.Message || "Logo updated successfully");
        }
      } catch (err) {
        showErrorToast("Failed to upload logo");
        setLoading(false);
        return;
      }
    } else if (logoState.CompIcon) {
      // No file to upload — keep selected logo (e.g. media library pick).
      const updatedCompanyData = {
        ...companyData,
        CompIcon: extractRelativePath(logoState.CompIcon),
        CompCoverImage: extractRelativePath(companyData.CompCoverImage),
        CompLatitude: companyData.CompLatitude.toString(),
        CompLongitude: companyData.CompLongitude.toString(),
      };
      const response = await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
      showSuccessToast(response.data.Message || "Logo updated successfully");
    }
    setIsLogoEditing(false);
    setLogoPreviousData(null);
    await fetchCompanyData(false);
    // Force scroll spy update after DOM updates - multiple calls to ensure it sticks
    setTimeout(() => {
    }, 100);
    setTimeout(() => {
    }, 300);
    setTimeout(() => {
    }, 500);
  } catch (error) {
    showErrorToast("Failed to update logo");
  } finally {
    setLoading(false);
  }
};
const saveImageSettings = async () => {
  try {
    setLoading(true);
    
    if (selectedImageType === "logo") {
      // Handle logo upload/removal
      if (!logoState.CompIcon && !logoState.logoFile && imagePreviousData?.CompIcon) {
        // Logo was removed
        try {
          await axiosInstance.delete(APIs.REMOVE_COMPANY_LOGO);
          showSuccessToast("Logo removed successfully");
          setCompanyData((prev) => ({ ...prev, CompIcon: "" }));
          setImageKey(Date.now());
          await fetchCompanyData(false);
          setIsImageEditing(false);
          setImagePreviousData(null);
          setLoading(false);
          setTimeout(() => {
          }, 100);
          return;
        } catch (err) {
          showErrorToast("Failed to remove logo");
          setLoading(false);
          return;
        }
      }
      
      if (logoState.logoFile) {
        const logoFormData = new FormData();
        logoFormData.append("ImageFile", logoState.logoFile);
        const baseURL = axiosInstance.defaults.baseURL || "";
        try {
          const logoResponse = await axiosInstance.post(
            APIs.POST_UPDATE_COMPANY_LOGO,
            logoFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const relativePath =
            logoResponse.data?.data?.filePath ||
            logoResponse.data?.data?.imageUrl ||
            logoResponse.data?.filePath ||
            logoResponse.data?.imageUrl ||
            logoResponse.data?.url;
          
          if (relativePath) {
            let fullImageUrl;
            if (relativePath.startsWith("http")) {
              fullImageUrl = relativePath;
            } else {
              const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
              const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
              fullImageUrl = `${baseUrlClean}${pathClean}`;
            }
            
            // Store only the relative path in database
            // Extract relative path for CompCoverImage to prevent cross-contamination
            const updatedCompanyData = {
              ...companyData,
              CompIcon: relativePath,
              CompCoverImage: extractRelativePath(companyData.CompCoverImage), // Extract relative path to prevent base URL duplication
              CompLatitude: companyData.CompLatitude.toString(),
              CompLongitude: companyData.CompLongitude.toString(),
            };
            const response = await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
            
            setCompanyData((prev) => ({ ...prev, CompIcon: fullImageUrl }));
            setLogoState({ CompIcon: fullImageUrl, logoFile: null });
            setImageKey(Date.now());
            showSuccessToast(response.data.Message || "Logo updated successfully");
          }
        } catch (err) {
          showErrorToast("Failed to upload logo");
          setLoading(false);
          return;
        }
      } else {
        const updatedCompanyData = {
          ...companyData,
          CompLatitude: companyData.CompLatitude.toString(),
          CompLongitude: companyData.CompLongitude.toString(),
        };
        await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
      }
    } else {
      // Handle cover image upload/removal
      if (!coverImageState.CompCoverImage && !coverImageState.coverImageFile && imagePreviousData?.CompCoverImage) {
        // Cover image was removed - use dedicated endpoint
        try {
          await axiosInstance.delete(APIs.REMOVE_COMPANY_COVER_IMAGE);
          showSuccessToast("Cover image removed successfully");
          setCompanyData((prev) => ({ ...prev, CompCoverImage: "" }));
          setCoverImageState({ CompCoverImage: "", coverImageFile: null });
          setImageKey(Date.now());
          await fetchCompanyData(false);
          setIsImageEditing(false);
          setImagePreviousData(null);
          setLoading(false);
          setTimeout(() => {
          }, 100);
          return;
        } catch (err) {
          showErrorToast("Failed to remove cover image");
          setLoading(false);
          return;
        }
      }
  
      if (coverImageState.coverImageFile) {
        const coverImageFormData = new FormData();
        coverImageFormData.append("ImageFile", coverImageState.coverImageFile);
        const baseURL = axiosInstance.defaults.baseURL || "";
        try {
          const coverImageResponse = await axiosInstance.post(
            APIs.POST_UPDATE_COMPANY_COVER_IMAGE,
            coverImageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const relativePath =
            coverImageResponse.data?.data?.filePath ||
            coverImageResponse.data?.data?.imageUrl ||
            coverImageResponse.data?.filePath ||
            coverImageResponse.data?.imageUrl ||
            coverImageResponse.data?.url;
          
          if (relativePath) {
            let fullImageUrl;
            if (relativePath.startsWith("http")) {
              fullImageUrl = relativePath;
            } else {
              const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
              const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
              fullImageUrl = `${baseUrlClean}${pathClean}`;
            }
            
            // Store relative path for CompCoverImage in database
            // Extract relative path for CompIcon to prevent cross-contamination
            const updatedCompanyData = {
              ...companyData,
              CompIcon: extractRelativePath(companyData.CompIcon), // Extract relative path to prevent base URL duplication
              CompCoverImage: relativePath,
              CompLatitude: companyData.CompLatitude.toString(),
              CompLongitude: companyData.CompLongitude.toString(),
            };
            await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
            setCompanyData((prev) => ({ 
              ...prev, 
              CompCoverImage: fullImageUrl
            }));
            setCoverImageState({ CompCoverImage: fullImageUrl, coverImageFile: null });
            setImageKey(Date.now());
          }
        } catch (err) {
          showErrorToast("Failed to upload cover image");
          setLoading(false);
          return;
        }
      } else {
        // Extract relative paths for both images to prevent base URL duplication
        const updatedCompanyData = {
          ...companyData,
          CompIcon: extractRelativePath(companyData.CompIcon),
          CompCoverImage: extractRelativePath(
            coverImageState.CompCoverImage || companyData.CompCoverImage
          ),
          CompLatitude: companyData.CompLatitude.toString(),
          CompLongitude: companyData.CompLongitude.toString(),
        };
        await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
      }
    }
    
    showSuccessToast(selectedImageType === "logo" ? "Logo updated successfully" : "Cover image updated successfully");
    setIsImageEditing(false);
    setImagePreviousData(null);
    await fetchCompanyData(false);
    setTimeout(() => {
    }, 100);
    setTimeout(() => {
    }, 300);
    setTimeout(() => {
    }, 500);
  } catch (error) {
    showErrorToast(`Failed to update ${selectedImageType === "logo" ? "logo" : "cover image"}`);
  } finally {
    setLoading(false);
  }
};
const saveCoverImageSettings = async () => {
  try {
    setLoading(true);
    const hadExistingCover = !!(
      companyData.CompCoverImage || coverImagePreviousData?.CompCoverImage
    );
    const isRemovingCover =
      !coverImageState.CompCoverImage &&
      !coverImageState.coverImageFile &&
      hadExistingCover;

    if (isRemovingCover) {
      try {
        await axiosInstance.delete(APIs.REMOVE_COMPANY_COVER_IMAGE);
        showSuccessToast("Cover image removed successfully");
        setCompanyData((prev) => ({ ...prev, CompCoverImage: "" }));
        setCoverImageState({ CompCoverImage: "", coverImageFile: null });
        setImageKey(Date.now());
        await fetchCompanyData(false);
        setIsCoverImageEditing(false);
        setCoverImagePreviousData(null);
        setLoading(false);
        return;
      } catch (err) {
        showErrorToast("Failed to remove cover image");
        setLoading(false);
        return;
      }
    }
    if (coverImageState.coverImageFile) {
      const coverImageFormData = new FormData();
      coverImageFormData.append("ImageFile", coverImageState.coverImageFile);
      const baseURL = axiosInstance.defaults.baseURL || "";
      try {
        const coverImageResponse = await axiosInstance.post(
          APIs.POST_UPDATE_COMPANY_COVER_IMAGE,
          coverImageFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const relativePath =
          coverImageResponse.data?.data?.filePath ||
          coverImageResponse.data?.data?.imageUrl ||
          coverImageResponse.data?.filePath ||
          coverImageResponse.data?.imageUrl ||
          coverImageResponse.data?.url;
        if (relativePath) {
          let fullImageUrl;
          if (relativePath.startsWith("http")) {
            fullImageUrl = relativePath;
          } else {
            const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
            const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
            fullImageUrl = `${baseUrlClean}${pathClean}`;
          }
          const updatedCompanyData = {
            ...companyData,
            CompIcon: extractRelativePath(companyData.CompIcon),
            CompCoverImage: relativePath,
            CompLatitude: companyData.CompLatitude.toString(),
            CompLongitude: companyData.CompLongitude.toString(),
          };
          await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
          setCompanyData((prev) => ({ ...prev, CompCoverImage: fullImageUrl }));
          setCoverImageState({ CompCoverImage: fullImageUrl, coverImageFile: null });
          setImageKey(Date.now());
          showSuccessToast("Cover image updated successfully");
        }
      } catch (err) {
        showErrorToast("Failed to upload cover image");
        setLoading(false);
        return;
      }
    } else if (coverImageState.CompCoverImage) {
      const updatedCompanyData = {
        ...companyData,
        CompIcon: extractRelativePath(companyData.CompIcon),
        // Respect currently selected cover image when no new file was uploaded
        // (e.g., choosing from media library).
        CompCoverImage: extractRelativePath(coverImageState.CompCoverImage),
        CompLatitude: companyData.CompLatitude.toString(),
        CompLongitude: companyData.CompLongitude.toString(),
      };
      await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
      showSuccessToast("Cover image updated successfully");
    }
    setIsCoverImageEditing(false);
    setCoverImagePreviousData(null);
    await fetchCompanyData(false);
  } catch (error) {
    showErrorToast("Failed to update cover image");
  } finally {
    setLoading(false);
  }
};

  const submitCompanyEmailChange = async () => {
    const ne = emailChangeNew.trim();
    if (!ne) {
      showErrorToast("Enter the new company email.");
      return;
    }
    try {
      setLoading(true);
      const res = await axiosInstance.post(APIs.POST_REQUEST_COMPANY_EMAIL_CHANGE, {
        compID: companyData.CompID,
        newEmail: ne,
        confirmEmail: ne,
      });
      if (res.status >= 400 || res.data?.status === false) {
        showErrorToast(res.data?.message || "Could not start email change.");
        return;
      }
      showSuccessToast(
        res.data?.message ||
          "Check your current company inbox for a confirmation link."
      );
      setIsCompanyEmailEditingInline(false);
      setEmailChangeNew("");
      await fetchCompanyData(false);
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Could not start email change."
      );
    } finally {
      setLoading(false);
    }
  };

const saveGeneralSettings = async () => {
    try {
      setLoading(true);
      const updatedCompanyData = {
        ...companyData,
        CompLatitude: companyData.CompLatitude.toString(),
        CompLongitude: companyData.CompLongitude.toString(),
      };
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_COMPANY_DATA,
        updatedCompanyData
      );
      showSuccessToast(
        response.data.Message || "General settings updated successfully"
      );
      setIsGeneralEditing(false);
      setGeneralPreviousData(null);
      await fetchCompanyData(false);
      // Force scroll spy update after DOM updates - multiple calls to ensure it sticks
      setTimeout(() => {
      }, 100);
      setTimeout(() => {
      }, 300);
      setTimeout(() => {
      }, 500);
    } catch (error) {
      showErrorToast("Failed to update general settings");
    } finally {
      setLoading(false);
    }
  };
const saveSocialLinksSettings = async () => {
  try {
    setLoading(true);
    const response = await axiosInstance.put(
      APIs.PUT_UPDATE_COMPANY_DATA,
      companyData
    );
    showSuccessToast(
      response.data.Message || "Social links updated successfully"
    );
    setIsSocialLinksEditing(false);
    setSocialLinksPreviousData(null);
    await fetchCompanyData(false);
    // Force scroll spy update after DOM updates - multiple calls to ensure it sticks
    setTimeout(() => {
    }, 100);
    setTimeout(() => {
    }, 300);
    setTimeout(() => {
    }, 500);
  } catch (error) {
    showErrorToast("Failed to update Social links");
  } finally {
    setLoading(false);
  }
};
const saveBusinessDetailsSettings = async () => {
  try {
    setLoading(true);
    const updatedCompanyData = {
      ...companyData,
      CompLatitude: companyData.CompLatitude.toString(),
      CompLongitude: companyData.CompLongitude.toString(),
    };
    const response = await axiosInstance.put(
      APIs.PUT_UPDATE_COMPANY_DATA,
      updatedCompanyData
    );
    showSuccessToast(
      response.data.Message || "Business details updated successfully"
    );
    setIsBusinessDetailsEditing(false);
    setBusinessDetailsPreviousData(null);
    await fetchCompanyData(false);
    // Force scroll spy update after DOM updates - multiple calls to ensure it sticks
    setTimeout(() => {
    }, 100);
    setTimeout(() => {
    }, 300);
    setTimeout(() => {
    }, 500);
  } catch (error) {
    showErrorToast("Failed to update business details");
  } finally {
    setLoading(false);
  }
};
const saveSecuritySettings = async () => {
  try {
    setLoading(true);
    const securityEmail = (companyData.CompRecoveryEmail || "").trim();
    const securityPhone = (companyData.CompRecoveryPhoneNumber || "").trim();
    const ooEmail = (companyData.S_OnlineOrderingEmail || "").trim();
    const ooPhone = (companyData.S_OnlineOrderingPhone || "").trim();

    const hadAny =
      !!(securityPreviousData?.CompRecoveryEmail || "").trim() ||
      !!(securityPreviousData?.CompRecoveryPhoneNumber || "").trim() ||
      !!(securityPreviousData?.S_OnlineOrderingEmail || "").trim() ||
      !!(securityPreviousData?.S_OnlineOrderingPhone || "").trim();

    if (securityEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(securityEmail)) {
      showErrorToast("Please enter a valid security contact email");
      setLoading(false);
      return;
    }
    if (ooEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ooEmail)) {
      showErrorToast("Please enter a valid contact email");
      setLoading(false);
      return;
    }

    // 1) Security contacts → company profile
    const updatedCompanyData = {
      ...companyData,
      CompRecoveryEmail: securityEmail,
      CompRecoveryPhoneNumber: securityPhone,
      CompLatitude: companyData.CompLatitude?.toString?.() ?? companyData.CompLatitude,
      CompLongitude: companyData.CompLongitude?.toString?.() ?? companyData.CompLongitude,
    };
    const companyResponse = await axiosInstance.put(
      APIs.PUT_UPDATE_COMPANY_DATA,
      updatedCompanyData
    );
    if (companyResponse.data?.status === false) {
      showErrorToast(
        companyResponse.data?.Message ||
          companyResponse.data?.message ||
          "Failed to save security contacts"
      );
      return;
    }

    // 2) Online ordering contacts → settings (add or update)
    const settingsResponse = await axiosInstance.post(
      APIs.POST_UPDATE_COMPANY_CONTACT_SETTINGS,
      {
        CompID,
        S_OnlineOrderingEmail: ooEmail || null,
        S_OnlineOrderingPhone: ooPhone || null,
      },
      { headers: { "Content-Type": "application/json" } }
    );

    if (!settingsResponse.data?.status) {
      showErrorToast(
        settingsResponse.data?.Message ||
          settingsResponse.data?.message ||
          "Security contacts saved, but online ordering contacts failed"
      );
      await fetchCompanyData(false);
      return;
    }

    const savedSettings = settingsResponse.data?.data;
    setCompanyData((prev) => ({
      ...prev,
      CompRecoveryEmail: securityEmail,
      CompRecoveryPhoneNumber: securityPhone,
      S_OnlineOrderingEmail: pickSettingsString(
        savedSettings?.s_OnlineOrderingEmail,
        savedSettings?.S_OnlineOrderingEmail,
        savedSettings?.onlineOrderingEmail,
        ooEmail
      ),
      S_OnlineOrderingPhone: pickSettingsString(
        savedSettings?.s_OnlineOrderingPhone,
        savedSettings?.S_OnlineOrderingPhone,
        savedSettings?.onlineOrderingPhone,
        ooPhone
      ),
    }));

    showSuccessToast(
      hadAny ? "Contacts updated successfully" : "Contacts added successfully"
    );
    setIsSecurityEditing(false);
    setSecurityPreviousData(null);
    await fetchCompanyData(false);
  } catch (error) {
    showErrorToast("Failed to save security settings");
  } finally {
    setLoading(false);
  }
};
  const saveField = async (name) => {
    if (name === "CompEmail") {
      showErrorToast("Use Change company email to update the company email.");
      return;
    }
    try {
      setLoading(true);
      if (name === "CompIcon") {
        if (!logoState.logoFile) {
          showErrorToast("Please select a logo to upload");
          setLoading(false);
          return;
        }
        const logoFormData = new FormData();
        logoFormData.append("ImageFile", logoState.logoFile);
        const baseURL = axiosInstance.defaults.baseURL || "";
        try {
          const logoResponse = await axiosInstance.post(
            APIs.POST_UPDATE_COMPANY_LOGO,
            logoFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const relativePath =
            logoResponse.data?.data?.filePath ||
            logoResponse.data?.data?.imageUrl ||
            logoResponse.data?.filePath ||
            logoResponse.data?.imageUrl ||
            logoResponse.data?.url;
          
          if (!relativePath) {
            showErrorToast("Failed to get uploaded logo URL");
            setLoading(false);
            return;
          }
          
          // Construct full URL by prepending baseURL (matching backend logic)
          // Backend service does: _baseUrl + staffImagePath
          // Handle both cases: baseURL with/without trailing slash, path with/without leading slash
          let fullImageUrl;
          if (relativePath.startsWith("http")) {
            fullImageUrl = relativePath;
          } else {
            const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
            const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
            fullImageUrl = `${baseUrlClean}${pathClean}`;
          }
          
          // Backend stores relative path (e.g., "uploads/filename.ext")
          // The service adds baseUrl when fetching, so we should store only the relative path
          // Store only the relative path in database (backend service will add baseUrl when fetching)
          // Extract relative path for CompCoverImage to prevent cross-contamination
          const updatedCompanyData = {
            ...companyData,
            CompIcon: relativePath, // Store relative path, not full URL
            CompCoverImage: extractRelativePath(companyData.CompCoverImage), // Extract relative path to prevent base URL duplication
            CompLatitude: companyData.CompLatitude.toString(),
            CompLongitude: companyData.CompLongitude.toString(),
          };
          await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
          
          // Update local state with full URL for display (but DB has relative path)
          setCompanyData((prev) => ({ ...prev, CompIcon: fullImageUrl }));
          setLogoState({ CompIcon: fullImageUrl, logoFile: null });
          setImageKey(Date.now()); // Force image refresh
          
          showSuccessToast("Company logo updated successfully");
          await fetchCompanyData(false);
          cancelEditingAfterSubmit();
        } catch (err) {
          showErrorToast("Failed to upload logo");
          setLoading(false);
          return;
        }
      } else if (name === "CompCoverImage") {
        if (!coverImageState.coverImageFile) {
          showErrorToast("Please select a cover image to upload");
          setLoading(false);
          return;
        }
        
        const coverImageFormData = new FormData();
        coverImageFormData.append("ImageFile", coverImageState.coverImageFile);
        const baseURL = axiosInstance.defaults.baseURL || "";
        try {
          const coverImageResponse = await axiosInstance.post(
            APIs.POST_UPDATE_COMPANY_COVER_IMAGE,
            coverImageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const relativePath =
            coverImageResponse.data?.data?.filePath ||
            coverImageResponse.data?.data?.imageUrl ||
            coverImageResponse.data?.filePath ||
            coverImageResponse.data?.imageUrl ||
            coverImageResponse.data?.url;
          
          if (!relativePath) {
            showErrorToast("Failed to get uploaded cover image URL");
            setLoading(false);
            return;
          }
          
          let fullImageUrl;
          if (relativePath.startsWith("http")) {
            fullImageUrl = relativePath;
          } else {
            const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
            const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
            fullImageUrl = `${baseUrlClean}${pathClean}`;
          }
          
          // Backend stores relative path and service adds baseUrl when fetching
          // Store relative path for CompCoverImage (service will add baseUrl)
          // Extract relative path for CompIcon to prevent cross-contamination
          const updatedCompanyData = {
            ...companyData,
            CompIcon: extractRelativePath(companyData.CompIcon), // Extract relative path to prevent base URL duplication
            CompCoverImage: relativePath, // Store relative path, service will add baseUrl
            CompLatitude: companyData.CompLatitude.toString(),
            CompLongitude: companyData.CompLongitude.toString(),
          };
          // Update via general company update endpoint to set CompCoverImage
          await axiosInstance.put(APIs.PUT_UPDATE_COMPANY_DATA, updatedCompanyData);
          setCompanyData((prev) => ({ 
            ...prev, 
            CompCoverImage: fullImageUrl // Use full URL for display
          }));
          setCoverImageState({ CompCoverImage: fullImageUrl, coverImageFile: null });
          setImageKey(Date.now()); // Force image refresh
          showSuccessToast("Company cover image updated successfully");
          await fetchCompanyData(false);
          cancelEditingAfterSubmit();
        } catch (err) {
          showErrorToast("Failed to upload cover image");
          setLoading(false);
          return;
        }
      } else {
        let updatedCompanyData = { ...companyData };
        if (name === "CompCountryCity") {
          updatedCompanyData = {
            ...updatedCompanyData,
            CompCountry: editValue.CompCountry,
            CompCity: editValue.CompCity,
          };
        } else if (name === "CompTimeZone") {
          updatedCompanyData = {
            ...updatedCompanyData,
            CompTimeZone: editValue.value,
            CompUTCTimeZone: editValue.label,
          };
        } else {
          updatedCompanyData[name] = editValue;
        }
        const alteredData = {
          ...updatedCompanyData,
          // Extract relative paths for both images to prevent base URL duplication
          CompIcon: extractRelativePath(updatedCompanyData.CompIcon),
          CompCoverImage: extractRelativePath(updatedCompanyData.CompCoverImage),
          CompLatitude: companyData.CompLatitude.toString(),
          CompLongitude: companyData.CompLongitude.toString(),
        };
        const response = await axiosInstance.put(
          APIs.PUT_UPDATE_COMPANY_DATA,
          alteredData
        );
        setCompanyData(updatedCompanyData);
        showSuccessToast(
          response.data.Message || `${name} updated successfully`
        );
        cancelEditingAfterSubmit();
      }
    } catch (error) {
      showErrorToast(`Failed to update ${name}`);
    } finally {
      setLoading(false);
    }
  };
  const getFieldIcon = (name) => {
    switch (name) {
      case "CompName":
        return "fa-building";
      case "CompDetail":
        return "fa-info-circle";
      case "CompPhone":
        return "fa-phone";
      case "CompEmail":
        return "fa-envelope";
      case "CompCountry":
        return "fa-flag";
      case "CompCity":
        return "fa-city";
      case "CompCountryCity":
        return "fa-globe";
      case "CompLanguage":
        return "fa-language";
      case "CompCurrency":
        return "fa-money-bill-wave";
      case "CompTimeZone":
        return "fa-clock";
      case "CompAddress":
        return "fa-map-marker-alt";
      case "CompFax":
        return "fa-fax";
      case "CompWebsite":
        return "fa-link";
      case "CompFacebooklink":
        return "fab fa-facebook-f"; // Brand icon
      case "CompInstagramLink":
        return "fab fa-instagram"; // Brand icon
      case "CompYoutubeLink":
        return "fab fa-youtube"; // Brand icon
      case "CompTikTokLink":
        return "fab fa-tiktok"; // Brand icon (requires Font Awesome 5.13.0+)
      case "CompGoogleReview":
        return "fa-star";
      case "CompIndustryType":
        return "fa-industry";
      case "CompSecCode":
        return "fa-key";
      case "CompanySlug":
        return "fa-link";
      case "CompLatitude":
        return "fa-map-pin";
      case "CompLongitude":
        return "fa-map-pin";
      case "CompPNotificationLimit_PerDay":
        return "fa-bell";
      case "CompMaxIPadLicense":
        return "fa-tablet-alt";
      case "CompMaxUserLicense":
        return "fa-users";
      case "WaiterAppMaxDevices":
        return "fa-mobile-alt";
      case "CompRegDate":
        return "fa-calendar-alt";
      case "CompExpireDate":
        return "fa-calendar-times";
      case "MobileAppExpireDate":
        return "fa-mobile";
      case "WebAppExpireDate":
        return "fa-globe";
      case "WaiterAppExpireDate":
        return "fa-concierge-bell";
      case "IsKitchenApp":
        return "fa-utensils";
      case "ISMobileApp":
        return "fa-mobile-alt";
      case "IsTVApp":
        return "fa-tv";
      case "ISWebApp":
        return "fa-globe";
      case "IsWaiterApp":
        return "fa-concierge-bell";
      case "CompRecoveryEmail":
      case "S_OnlineOrderingEmail":
        return "fa-envelope";
      case "CompRecoveryPhoneNumber":
      case "S_OnlineOrderingPhone":
        return "fa-phone";
      case "CompIcon":
        return "fa-image";
      default:
        return "fa-building";
    }
  };
  const renderField = (
    label,
    name,
    value,
    isEditable = true,
    sectionEditing = false,
    hideIndividualEdit = false,
    hidden = false,
    colClass = "col-md-6",
    dense = false
  ) => {
    if (hidden) {
      return null; // Skip rendering this field entirely, but keep the code in place
    }
    const isEditing = isEditable && (editingField === name || sectionEditing);
    const getCurrentValue = () => {
      if (sectionEditing) {
        if (name === "CompCountryCity") {
          return {
            CompCountry: companyData.CompCountry,
            CompCity: companyData.CompCity,
          };
        }
        return companyData[name];
      }
      return editValue;
    }
    return (
      <div className={`${colClass} profile-field${dense ? " profile-field--dense" : ""}`} key={name}>
        <div className="profile-field__label">
          <i className={`fas ${getFieldIcon(name)} profile-field__label-icon`} aria-hidden="true"></i>
          <span className="profile-field__label-text">{label}</span>
        </div>
        <div>
            {isEditing ? (
              <div className="mt-2">
                {name === "UpdateProCPriceAgainstMasterProductCPriceUpdate" ||
                name === "IsKitchenApp" ||
                name === "ISMobileApp" ||
                name === "IsTVApp" ||
                name === "ISWebApp" ||
                name === "IsWaiterApp" ? (
                  <ToggleSwitch
                    isChecked={sectionEditing ? companyData[name] : editValue}
                    onToggle={(checked) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: checked,
                        }));
                      } else {
                        handleToggleChange(name, checked);
                      }
                    }}
                    label={label}
                    name={name}
                  />
                ) : name === "CompCurrency" ? (
                  <Select
                    options={currencies.map((c) => ({
                      value: c.value,
                      label: c.text,
                    }))}
                    value={currencies
                      .filter((c) => c.value === getCurrentValue())
                      .map((c) => ({ value: c.text, label: c.text }))}
                    onChange={(option) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: option ? option.value : "",
                        }));
                      } else {
                        handleSelectChange(name, option);
                      }
                    }}
                    placeholder="Select Currency"
                    isClearable
                    className="text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#374151" : "#ffffff",
                        borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          borderColor: isDarkMode ? "#6b7280" : "#9ca3af",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid #374151"
                          : "1px solid #e5e7eb",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? "#374151"
                            : "#f3f4f6"
                          : isDarkMode
                          ? "#1f2937"
                          : "#ffffff",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                ) : name === "CompTimeZone" ? (
                  <Select
                    options={timeZones.map((c) => ({
                      value: c.value,
                      label: c.text,
                    }))}
                    value={timeZones
                      .filter((c) => c.value === getCurrentValue())
                      .map((c) => ({ value: c.value, label: c.text }))}
                    onChange={(option) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: option ? option.value : "",
                          CompUTCTimeZone: option ? option.label : "",
                        }));
                      } else {
                        handleTimeZoneSelectChange(name, option);
                      }
                    }}
                    placeholder="Select Time Zone"
                    isClearable
                    className="text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#374151" : "#ffffff",
                        borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          borderColor: isDarkMode ? "#6b7280" : "#9ca3af",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid #374151"
                          : "1px solid #e5e7eb",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? "#374151"
                            : "#f3f4f6"
                          : isDarkMode
                          ? "#1f2937"
                          : "#ffffff",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                ) : name === "CompLanguage" ? (
                  <Select
                    options={languages}
                    value={
                      languages.find(
                        (lang) => lang.value === getCurrentValue()
                      ) || null
                    }
                    onChange={(option) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: option ? option.value : "",
                        }));
                      } else {
                        handleSelectChange(name, option);
                      }
                    }}
                    placeholder="Select Language"
                    isClearable
                    className="text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#374151" : "#ffffff",
                        borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          borderColor: isDarkMode ? "#6b7280" : "#9ca3af",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid #374151"
                          : "1px solid #e5e7eb",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? isDarkMode
                            ? "#374151"
                            : "#f3f4f6"
                          : isDarkMode
                          ? "#1f2937"
                          : "#ffffff",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                ) : name === "CompAddress" ? (
                  <textarea
                    className={`w-100 rounded border ${
                      dense ? "p-2" : "p-3"
                    } ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        : "border-gray-300 bg-gray-50 focus:ring-2 focus:ring-[#232B2B] focus:border-[#232B2B]"
                    }`}
                    style={{
                      width: "100%",
                      backgroundColor: isDarkMode ? "#374151" : "#f9fafb",
                      borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                    rows={dense ? 3 : 4}
                    value={getCurrentValue()}
                    onChange={(e) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: e.target.value,
                        }));
                      } else {
                        setEditValue(e.target.value);
                      }
                    }}
                    placeholder="Enter Company Address"
                  />
                ) : name === "CompIcon" ? (
                  <div className="mt-3">
                    <ImageUpload
                      key={imageKey}
                      imageUrl={logoState.CompIcon || companyData.CompIcon}
                      imageFile={logoState.logoFile}
                      setImage={(url, file) => {
                        setLogoState({ CompIcon: url, logoFile: file });
                        setEditValue(url);
                      }}
                      onRemove={handleRemoveLogo}
                      height="120px"
                      width="150px"
                      marginLeft="0px"
                      buttonText="Change Logo"
                      uploadText="Upload Logo"
                      showChangeButton={false}
                    />
                  </div>
                ) : name === "CompCoverImage" ? (
                  <div className="mt-3">
                    <ImageUpload
                      key={imageKey}
                      imageUrl={coverImageState.CompCoverImage || companyData.CompCoverImage}
                      imageFile={coverImageState.coverImageFile}
                      setImage={(url, file) => {
                        setCoverImageState({ CompCoverImage: url, coverImageFile: file });
                        setEditValue(url);
                      }}
                      onRemove={handleRemoveCoverImage}
                      height="120px"
                      width="100%"
                      marginLeft="0px"
                      buttonText="Change Cover Image"
                      uploadText="Upload Cover Image"
                      showChangeButton={false}
                      cropAspect={16 / 9}
                      cropModalTitle="Crop cover image (16:9)"
                    />
                  </div>
                ) : name === "CompCountryCity" ? (
                  <CountryCitySelector
                    selectedCountry={
                      sectionEditing
                        ? companyData.CompCountry
                        : editValue.CompCountry
                    }
                    selectedCity={
                      sectionEditing ? companyData.CompCity : editValue.CompCity
                    }
                    setSelectedCity={(city) => {
                      setSelectedCity(city);
                      if (sectionEditing) {
                        setCompanyData((prev) => ({ ...prev, CompCity: city }));
                      } else {
                        setEditValue((prev) => ({ ...prev, CompCity: city }));
                      }
                    }}
                    onChange={(updatedData) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          CompCountry: updatedData.CompCountry,
                          CompCity: updatedData.CompCity || "",
                        }));
                      } else {
                        setEditValue((prev) => ({
                          ...prev,
                          CompCountry: updatedData.CompCountry,
                          CompCity: updatedData.CompCity || "",
                        }));
                      }
                    }}
                    setLoading={null}
                    className="d-flex gap-4 w-100"
                    countryClassName="flex-1 min-w-0"
                    cityClassName="flex-1 min-w-0"
                    inlineLayout={true}
                    selectedValueIsLabel={true}
                    countryFieldName="CompCountry"
                    cityFieldName="CompCity"
                    isDarkMode={isDarkMode}
                  />
                ) : name === "CompRegDate" ||
                  name === "CompExpireDate" ||
                  name === "MobileAppExpireDate" ||
                  name === "WebAppExpireDate" ||
                  name === "WaiterAppExpireDate" ? (
                  <FormInput
                    type="date"
                    readOnly={false}
                    value={getCurrentValue()}
                    onchange={(e) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: e.target.value,
                        }));
                      } else {
                        setEditValue(e.target.value);
                      }
                    }}
                    placeholder={`Enter ${label}`}
                    style={{
                      borderRadius: "8px",
                      border: isDarkMode
                        ? "1px solid #4b5563"
                        : "1px solid #D9D9D9",
                      background: isDarkMode ? "#374151" : "#F9F9F9",
                      height: "44px",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                ) : (
                  <FormInput
                    type={
                      name.includes("Email")
                        ? "email"
                        : name.includes("Phone") || name.includes("Fax")
                        ? "tel"
                        : name.includes("Latitude") ||
                          name.includes("Longitude") ||
                          name.includes("MinOrderAmount") ||
                          name.includes("CompPNotificationLimit_PerDay") ||
                          name.includes("CompMaxIPadLicense") ||
                          name === "CompMaxUserLicense" ||
                          name.includes("WaiterAppMaxDevices") ||
                          name.includes("LocationLicences")
                        ? "number"
                        : "text"
                    }
                    value={getCurrentValue()}
                    readOnly={false}
                    prepend={
                      (name === "CompPhone" ||
                        name === "CompRecoveryPhoneNumber" ||
                        name === "S_OnlineOrderingPhone") &&
                      companyDialCode
                        ? companyDialCode
                        : undefined
                    }
                    onchange={(e) => {
                      if (sectionEditing) {
                        setCompanyData((prev) => ({
                          ...prev,
                          [name]: e.target.value,
                        }));
                      } else {
                        setEditValue(e.target.value);
                      }
                    }}
                    placeholder={`Enter ${label}`}
                    step={
                      name.includes("Latitude") || name.includes("Longitude")
                        ? "0.000001"
                        : name.includes("CompPNotificationLimit_PerDay")
                        ? "10"
                        : "1"
                    }
                    style={{
                      borderRadius: "8px",
                      border: isDarkMode
                        ? "1px solid #4b5563"
                        : "1px solid #D9D9D9",
                      background: isDarkMode ? "#374151" : "#F9F9F9",
                      height: "44px",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                )}
                {!sectionEditing && (
                  <div className="mt-3 flex gap-3">
                    <button
                      className="btn px-3 py-2 rounded text-white"
                      style={{
                        backgroundColor: themeColor,
                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                        boxShadow: `0 2px 8px ${themeColor}40`,
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                      }}
                      onClick={() => saveField(name)}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className={`btn px-3 py-2 rounded ${
                        isDarkMode
                          ? "bg-gray-600 text-white hover:bg-gray-500"
                          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                      }`}
                      style={{
                        backgroundColor: isDarkMode ? "#4b5563" : "#e5e7eb",
                        color: isDarkMode ? "#ffffff" : "#1f2937",
                      }}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {name === "UpdateProCPriceAgainstMasterProductCPriceUpdate" ||
                name === "IsKitchenApp" ||
                name === "ISMobileApp" ||
                name === "IsTVApp" ||
                name === "ISWebApp" ||
                name === "IsWaiterApp" ? (
                  <span className={`profile-field__value profile-field__value--bool${value ? " is-on" : " profile-field__value--empty"}`}>
                    {value ? "Enabled" : "Disabled"}
                  </span>
                ) : name === "CompRegDate" ||
                  name === "CompExpireDate" ||
                  name === "MobileAppExpireDate" ||
                  name === "WebAppExpireDate" ||
                  name === "WaiterAppExpireDate" ? (
                  renderFieldValue(value ? moment(value).format("DD/MM/YYYY") : null)
                ) : name === "CompCountryCity" ? (
                  renderFieldValue(
                    [companyData.CompCountry, companyData.CompCity].filter(Boolean).join(", ") || null
                  )
                ) : name === "CompLanguage" ? (
                  renderFieldValue(languages.find((lang) => lang.value === value)?.label || null)
                ) : name === "CompTimeZone" ? (
                  renderFieldValue(timeZones.find((tz) => tz.value === value)?.text || null)
                ) : name === "CompUTCTimeZone" ? (
                  renderFieldValue(value)
                ) : name === "CompIcon" ? (
                  value ? (
                    <div className="profile-field__value d-flex align-items-center gap-2">
                      <img
                        src={`${value}?t=${imageKey}`}
                        alt="Company Logo"
                        className="profile-field__logo-preview"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <span>Logo uploaded</span>
                    </div>
                  ) : (
                    renderFieldValue(null)
                  )
                ) : name === "CompDetail" || name === "CompAddress" ? (
                  renderFieldValue(value, { multiline: true })
                ) : name === "MinOrderAmount" ? (
                  renderFieldValue(
                    value === "" || value === null || value === undefined
                      ? null
                      : `${getCurrencySymbol(companyData.CompCurrency || "GBP")}${value}`
                  )
                ) : urlFields.has(name) ? (
                  renderFieldValue(value, {
                    href: isHttpUrl(value) ? value.trim() : null,
                  })
                ) : name === "CompPhone" ||
                  name === "CompRecoveryPhoneNumber" ||
                  name === "S_OnlineOrderingPhone" ? (
                  renderFieldValue(
                    value
                      ? companyDialCode
                        ? `${companyDialCode} ${value}`
                        : value
                      : null
                  )
                ) : (
                  renderFieldValue(value)
                )}
            </div>
          )}
        </div>
      </div>
    );
  };
  /** Business email strip — view or inline edit (confirmation-link flow). Shown in Business Details. */
  const renderCompanyEmailBanner = () => {
    const footerNote = (
      <div className="profile-email-banner__note">
        Changes use a confirmation link sent to your <strong>current</strong>{" "}
        inbox; the new address is saved only after you open that link.
      </div>
    );

    const iconCircle = (
      <div className="profile-email-banner__icon" aria-hidden="true">
        <i
          className={`fas ${isCompanyEmailEditingInline ? "fa-envelope-open-text" : "fa-at"}`}
        />
      </div>
    );

    if (isCompanyEmailEditingInline) {
      return (
        <div className="profile-email-banner">
          <div className="profile-email-banner__accent" aria-hidden />
          <div className="p-3 ps-4 pe-3">
            <div className="row g-3 align-items-lg-center">
              <div className="col-12 col-lg-1 d-flex justify-content-center justify-content-lg-start align-items-center pt-lg-1">
                {iconCircle}
              </div>
              <div className="col-12 col-lg-6 min-w-0">
                <div className="profile-email-banner__eyebrow">New email address</div>
                <FormInput
                  type="email"
                  value={emailChangeNew}
                  onchange={(e) => setEmailChangeNew(e.target.value)}
                  placeholder="name@company.com"
                  style={{
                    borderRadius: "10px",
                    border: isDarkMode ? "1px solid #4b5563" : "1px solid #cbd5e1",
                    background: isDarkMode ? "#0f172a" : "#ffffff",
                    height: "46px",
                    color: isDarkMode ? "#f1f5f9" : "#0f172a",
                    fontFamily:
                      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    fontSize: "14px",
                  }}
                />
                {companyData.CompEmail ? (
                  <p
                    className="small mb-0 mt-2"
                    style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                  >
                    Confirmation link will be sent to{" "}
                    <strong style={{ color: themeColor }}>
                      {companyData.CompEmail}
                    </strong>
                  </p>
                ) : null}
              </div>
              <div className="col-12 col-lg-5 d-flex flex-column flex-sm-row flex-wrap gap-2 justify-content-center justify-content-lg-end align-items-stretch align-items-lg-center">
                <button
                  type="button"
                  className="btn profile-btn profile-btn--ghost"
                  disabled={loading}
                  onClick={() => {
                    setIsCompanyEmailEditingInline(false);
                    setEmailChangeNew("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn profile-btn profile-btn--primary text-white"
                  disabled={loading}
                  onClick={submitCompanyEmailChange}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2" style={{ fontSize: "12px" }} />
                      Send confirmation link
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          {footerNote}
        </div>
      );
    }

    return (
      <div className="profile-email-banner">
        <div className="profile-email-banner__accent" aria-hidden />
        <div className="p-3 ps-4 pe-3">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-lg-1 d-flex justify-content-center justify-content-lg-start">
              {iconCircle}
            </div>
            <div className="col-12 col-lg-7 min-w-0 text-center text-lg-start">
              <div className="profile-email-banner__eyebrow">Business email</div>
              <div
                className={`profile-email-banner__email${companyData.CompEmail ? " has-value" : ""}`}
                title={companyData.CompEmail}
              >
                {companyData.CompEmail || "Not set — use Edit to add"}
              </div>
              {isPendingEmailChangeActive(
                companyData.PendingCompEmail,
                companyData.CompEmailChangeExpiresUtc
              ) ? (
                <div
                  className="small mt-2 text-center text-lg-start d-inline-block"
                  style={{ color: isDarkMode ? "#fbbf24" : "#b45309" }}
                >
                  Pending: <strong>{companyData.PendingCompEmail}</strong>
                  {companyData.CompEmailChangeExpiresUtc
                    ? ` · expires ${moment.utc(companyData.CompEmailChangeExpiresUtc).format("MMM D, h:mm A")} UTC`
                    : ""}
                </div>
              ) : null}
            </div>
            <div className="col-12 col-lg-4 d-flex justify-content-center justify-content-lg-end">
              <button
                type="button"
                className="btn d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill profile-btn profile-btn--ghost"
                title="Change company email"
                aria-label="Change company email"
                disabled={loading}
                onClick={() => {
                  setEmailChangeNew(
                    isPendingEmailChangeActive(
                      companyData.PendingCompEmail,
                      companyData.CompEmailChangeExpiresUtc
                    )
                      ? companyData.PendingCompEmail
                      : ""
                  );
                  setIsCompanyEmailEditingInline(true);
                }}
                style={{
                  borderColor: themeColor,
                  color: themeColor,
                  backgroundColor: `${themeColor}14`,
                }}
              >
                <i className="fas fa-pencil-alt" style={{ fontSize: "12px" }} />
                Edit email
              </button>
            </div>
          </div>
        </div>
        {footerNote}
      </div>
    );
  };

  const renderFieldRow = (fields, options = {}) => {
    const { compact = false, dense = false } = options;
    const visibleFields = fields.filter((field) => !field.hidden);
    if (visibleFields.length === 0) {
      return null;
    }
    const pad = compact ? 8 : 16;
    return (
      <div
        className="list-group-item"
        style={{ borderBottom: "none", paddingBottom: `${pad}px`, paddingTop: `${pad}px` }}
      >
        <div className={`row ${compact ? "g-2" : "g-3"}`}>
          {visibleFields.map((field) =>
            renderField(
              field.label,
              field.name,
              field.value,
              field.isEditable,
              field.sectionEditing,
              field.hideIndividualEdit,
              field.hidden,
              field.colClass || "col-md-6",
              dense
            )
          )}
        </div>
      </div>
    );
  };
  // Full-page loader only until core profile fields are present; saves use section loader below.
  const profileContentReady = !!(companyData.CompName || companyData.CompEmail);
  if (loading && !profileContentReady) {
    return (
      <div
        className={`min-h-screen font-sans ${
          isDarkMode ? "bg-gray-900" : "bg-gray-200"
        }`}
        style={{
          backgroundColor: isDarkMode ? "#1a1a1a" : "#f3f4f6",
          color: isDarkMode ? "#ffffff" : "#000000",
        }}
      >
        <GridSectionLoader loading={true} minHeight="100vh" />
      </div>
    );
  }
  if (!companyData.CompID) {
    return (
      <div
        className={`min-h-screen font-sans ${
          isDarkMode ? "bg-gray-900" : "bg-gray-200"
        }`}
        style={{
          backgroundColor: isDarkMode ? "#1a1a1a" : "#f3f4f6",
          color: isDarkMode ? "#ffffff" : "#000000",
        }}
      >
        <GridSectionLoader loading={true} minHeight="100vh" />
      </div>
    );
  }
  return (
    <>
      <div
        className={`container-fluid px-3 px-md-4 py-3 profile-page${isDarkMode ? " profile-page--dark" : ""}`}
        style={profileThemeStyle}
      >
        <div className="d-flex align-items-center mb-3 profile-page-header">
          <div>
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Account Info</li>
            </ol>
            <h1 className="page-header mb-0">
              <i className="fa fa-user me-2" aria-hidden="true"></i>
              Account Info
            </h1>
            <p className="profile-page-subtitle">
              Manage your company profile, branding, and account settings.
            </p>
          </div>
        </div>

        <GridSectionLoader loading={loading} minHeight="400px">
        <div className="row" style={{ marginLeft: 0, marginRight: 0 }}>
          <div className="col-12" id="bsSpyContent" style={{ flex: '1', maxWidth: 'none' }}>
            {companyData.CompID ? (
              <>
                <section
                  id="companyLogo"
                  className="card border-0 mb-3 profile-section"
                 
                >
                  <div className="card-body">
                    {renderSectionHeading(
                      "Company Images",
                      "Cover photo and logo — shown like a profile header across ServeIQ."
                    )}
                    <div className="pt-1">
                      {(() => {
                        const currentLogoUrl =
                          logoState.CompIcon !== undefined
                            ? logoState.CompIcon || ""
                            : companyData.CompIcon && companyData.CompIcon !== ""
                              ? companyData.CompIcon
                              : "";
                        const currentCoverUrl =
                          coverImageState.CompCoverImage !== undefined
                            ? coverImageState.CompCoverImage || ""
                            : companyData.CompCoverImage && companyData.CompCoverImage !== ""
                              ? companyData.CompCoverImage
                              : "";
                        const logoDirty =
                          logoState.logoFile !== null ||
                          (logoState.CompIcon || "") !== (companyData.CompIcon || "");
                        const coverDirty =
                          coverImageState.coverImageFile !== null ||
                          (coverImageState.CompCoverImage || "") !==
                            (companyData.CompCoverImage || "");

                        return (
                          <div className="profile-fb-card">
                            <div className="profile-fb-cover">
                              <div className="profile-fb-cover__media">
                                <ImageUpload
                                  key={`${imageKey}-cover`}
                                  imageUrl={currentCoverUrl}
                                  imageFile={coverImageState.coverImageFile}
                                  setImage={(url, file) =>
                                    setCoverImageState({
                                      CompCoverImage: url,
                                      coverImageFile: file,
                                    })
                                  }
                                  onRemove={handleRemoveCoverImage}
                                  height="100%"
                                  width="100%"
                                  marginLeft="0px"
                                  buttonText="New Cover"
                                  uploadText="Add cover photo"
                                  useGrid={false}
                                  centerButton={true}
                                  compactButton={true}
                                  showChangeButton={false}
                                  cropAspect={16 / 9}
                                  cropModalTitle="Crop cover image (16:9)"
                                  noPreviewMargin={true}
                                  expandPreview={true}
                                  expandObjectFit="cover"
                                  clampPreviewToParent={true}
                                />
                              </div>
                              {!currentCoverUrl ? (
                                <p className="profile-fb-cover__hint">
                                  Cover must be cropped to 16:9 (e.g. 1600×900) to match online ordering.
                                </p>
                              ) : null}
                            </div>

                            <div className="profile-fb-body">
                              <div className="profile-fb-body__main">
                            <div className="profile-fb-identity">
                              <div className="profile-fb-avatar" title="Company logo">
                                <div className="profile-fb-avatar__ring">
                                  <div className="profile-fb-avatar__media">
                                    <ImageUpload
                                      key={`${imageKey}-logo`}
                                      imageUrl={currentLogoUrl}
                                      imageFile={logoState.logoFile}
                                      setImage={(url, file) =>
                                        setLogoState({ CompIcon: url, logoFile: file })
                                      }
                                      onRemove={handleRemoveLogo}
                                      height="100%"
                                      width="100%"
                                      marginLeft="0px"
                                      buttonText="New Logo"
                                      uploadText="Logo"
                                      useGrid={false}
                                      centerButton={true}
                                      compactButton={true}
                                      showChangeButton={false}
                                      showRemoveButton={false}
                                      cropAspect={0}
                                      cropModalTitle="Crop logo"
                                      noPreviewMargin={true}
                                      expandPreview={true}
                                      expandObjectFit="contain"
                                      openLibraryRef={logoOpenLibraryRef}
                                    />
                                  </div>
                                </div>
                                {currentLogoUrl ? (
                                  <button
                                    type="button"
                                    className="profile-fb-avatar__badge profile-fb-avatar__badge--remove"
                                    onClick={handleRemoveLogo}
                                    aria-label="Remove logo"
                                    title="Remove logo"
                                  >
                                    <i className="fa fa-times" aria-hidden="true" />
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="profile-fb-avatar__badge profile-fb-avatar__badge--edit"
                                  onClick={() => logoOpenLibraryRef.current?.()}
                                  aria-label="Change logo"
                                  title="Change logo"
                                  style={{ backgroundColor: themeColor }}
                                >
                                  <i className="fa fa-camera" aria-hidden="true" />
                                </button>
                              </div>

                              <div className="profile-fb-meta">
                                <h3 className="profile-fb-meta__name">
                                  {companyData.CompName || "Your company"}
                                </h3>

                                {(logoDirty || coverDirty) && (
                                  <div className="profile-fb-actions profile-fb-actions--inline">
                                    {logoDirty ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                                          onClick={() => {
                                            setLogoState({
                                              CompIcon: companyData.CompIcon || "",
                                              logoFile: null,
                                            });
                                          }}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-times" style={{ fontSize: "10px" }} />
                                          Cancel logo
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm profile-btn profile-btn--primary text-white d-inline-flex align-items-center gap-2"
                                          onClick={saveLogoSettings}
                                          disabled={loading}
                                        >
                                          {loading ? (
                                            <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full" />
                                          ) : (
                                            <i className="fas fa-save" style={{ fontSize: "10px" }} />
                                          )}
                                          {loading ? "Saving..." : "Save logo"}
                                        </button>
                                      </>
                                    ) : null}
                                    {coverDirty ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                                          onClick={() => {
                                            setCoverImageState({
                                              CompCoverImage: companyData.CompCoverImage || "",
                                              coverImageFile: null,
                                            });
                                          }}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-times" style={{ fontSize: "10px" }} />
                                          Cancel cover
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm profile-btn profile-btn--primary text-white d-inline-flex align-items-center gap-2"
                                          onClick={saveCoverImageSettings}
                                          disabled={loading}
                                        >
                                          {loading ? (
                                            <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full" />
                                          ) : (
                                            <i className="fas fa-save" style={{ fontSize: "10px" }} />
                                          )}
                                          {loading ? "Saving..." : "Save cover"}
                                        </button>
                                      </>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="px-3 pb-3 profile-fb-hints">
                              <p
                                className="small mb-0"
                                style={{
                                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                                  fontSize: "10px",
                                  lineHeight: 1.4,
                                }}
                              >
                                Logo: 300×300px or larger recommended · Cover: fixed 16:9 crop (matches online ordering).
                                Click the cover or logo to change.
                              </p>
                            </div>
                              </div>

                              <aside className="profile-fb-aside" aria-label="Company licenses and slug">
                                <div className="profile-fb-licenses">
                                  <div className="profile-fb-licenses__item">
                                    <span className="profile-fb-licenses__label">
                                      <i className="fas fa-bell" style={{ color: themeColor }} aria-hidden="true" />
                                      Admin SMS credits
                                    </span>
                                    <span className="profile-fb-licenses__value">
                                      {companyData.CompMonthlySmsLimit ?? "—"}
                                    </span>
                                  </div>
                                  <div className="profile-fb-licenses__item">
                                    <span className="profile-fb-licenses__label">
                                      <i className="fas fa-users" style={{ color: themeColor }} aria-hidden="true" />
                                      Max users
                                    </span>
                                    <span className="profile-fb-licenses__value">
                                      {companyData.CompMaxUserLicense ?? "—"}
                                    </span>
                                  </div>
                                  <div className="profile-fb-licenses__item">
                                    <span className="profile-fb-licenses__label">
                                      <i className="fas fa-calendar-alt" style={{ color: themeColor }} aria-hidden="true" />
                                      Reg. start
                                    </span>
                                    <span className="profile-fb-licenses__value">
                                      {companyData.CompRegDate
                                        ? moment(companyData.CompRegDate).format("DD/MM/YYYY")
                                        : "—"}
                                    </span>
                                  </div>
                                  <div className="profile-fb-licenses__item">
                                    <span className="profile-fb-licenses__label">
                                      <i className="fas fa-calendar-check" style={{ color: themeColor }} aria-hidden="true" />
                                      Subs exp. date
                                    </span>
                                    <span className="profile-fb-licenses__value">
                                      {companyData.CompExpireDate
                                        ? moment(companyData.CompExpireDate).format("DD/MM/YYYY")
                                        : "—"}
                                    </span>
                                  </div>
                                  <div className="profile-fb-licenses__item profile-fb-licenses__item--slug">
                                    <span className="profile-fb-licenses__label">
                                      <i className="fas fa-link" style={{ color: themeColor }} aria-hidden="true" />
                                      Company slug
                                    </span>
                                    <span
                                      className="profile-fb-licenses__value"
                                      title={companyData.CompanySlug || undefined}
                                    >
                                      {companyData.CompanySlug || "—"}
                                    </span>
                                  </div>
                                </div>
                              </aside>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </section>
                <section
                  id="general"
                  className="card border-0 mb-3 profile-section"
                 
                >
                  <div className="card-body">
                    {renderSectionHeading(
                      "General",
                      "View and update your company general information."
                    )}
                  <div className="mb-0">
                    {!isGeneralEditing ? (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: 'none' }}>
                            {renderFieldRow([
                          {
                            label: "Company Name",
                            name: "CompName",
                            value: companyData.CompName,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                            colClass: "col-12 col-md-6",
                          },
                          {
                            label: "Contact Number",
                            name: "CompPhone",
                            value: companyData.CompPhone,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                            colClass: "col-12 col-md-6",
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Company Description",
                            name: "CompDetail",
                            value: companyData.CompDetail,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                            colClass: "col-12",
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Country",
                            name: "CompCountry",
                            value: companyData.CompCountry,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "City",
                            name: "CompCity",
                            value: companyData.CompCity,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        {/* Fax field hidden from General section
                        {renderFieldRow([
                          {
                            label: "Fax",
                            name: "CompFax",
                            value: companyData.CompFax,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        */}
                        {renderFieldRow([
                          {
                            label: "Currency",
                            name: "CompCurrency",
                            value: companyData.CompCurrency,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Time Zone",
                            name: "CompTimeZone",
                            value: companyData.CompTimeZone,
                            isEditable: true,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        {/* Company Slug moved to profile header panel
                        {renderFieldRow([
                          {
                            label: "Company Slug",
                            name: "CompanySlug",
                            value: companyData.CompanySlug,
                            isEditable: false,
                            sectionEditing: isGeneralEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        */}
                        <div className="list-group-item" style={{ borderBottom: 'none', paddingBottom: '10px', paddingTop: '10px' }}>
                          <div className="row g-2">
                            <div className="col-12">
                              {renderField(
                                "Address",
                                "CompAddress",
                                companyData.CompAddress,
                                true,
                                isGeneralEditing,
                                true,
                                false,
                                "col-12",
                                true
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="list-group-item profile-action-row" style={{ borderBottom: 'none', paddingTop: '10px', paddingBottom: '12px' }}>
                          <div className="d-flex justify-content-end">
                            <button
                              type="button"
                              className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                              onClick={startGeneralEditing}
                            >
                              <i className="fas fa-pen" style={{ fontSize: "10px" }} />
                              Edit
                            </button>
                          </div>
                        </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: "none" }}>
                        {renderFieldRow([
                          {
                            label: "Company Name",
                            name: "CompName",
                            value: companyData.CompName,
                            isEditable: true,
                            sectionEditing: true,
                            colClass: "col-12 col-md-6",
                          },
                          {
                            label: "Contact Number",
                            name: "CompPhone",
                            value: companyData.CompPhone,
                            isEditable: true,
                            sectionEditing: true,
                            colClass: "col-12 col-md-6",
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Company Description",
                            name: "CompDetail",
                            value: companyData.CompDetail,
                            isEditable: true,
                            sectionEditing: true,
                            colClass: "col-12",
                          },
                        ], { compact: true, dense: true })}
                        <div className="list-group-item" style={{ borderBottom: "none", paddingTop: "10px", paddingBottom: "10px" }}>
                          <div className="col-12 px-0">
                            <div className="d-flex align-items-center mb-1">
                              <i
                                className={`fas fa-globe me-2`}
                                style={{ color: themeColor, fontSize: "12px" }}
                              ></i>
                              <span
                                className="fw-semibold"
                                style={{ fontSize: "12px", color: isDarkMode ? "#94a3b8" : "#64748b" }}
                              >
                                Country & City
                              </span>
                            </div>
                            <CountryCitySelector
                              selectedCountry={companyData.CompCountry}
                              selectedCity={companyData.CompCity}
                              setSelectedCity={(city) => {
                                setSelectedCity(city);
                                setCompanyData((prev) => ({ ...prev, CompCity: city }));
                              }}
                              onChange={(updatedData) => {
                                setCompanyData((prev) => ({
                                  ...prev,
                                  CompCountry: updatedData.CompCountry,
                                  CompCity: updatedData.CompCity || "",
                                }));
                              }}
                              setLoading={null}
                              className="d-flex gap-2 w-100"
                              countryClassName="flex-1 min-w-0"
                              cityClassName="flex-1 min-w-0"
                              inlineLayout={true}
                              selectedValueIsLabel={true}
                              countryFieldName="CompCountry"
                              cityFieldName="CompCity"
                              isDarkMode={isDarkMode}
                            />
                          </div>
                        </div>
                        {/* Fax field hidden from General section
                        {renderFieldRow([
                          {
                            label: "Fax",
                            name: "CompFax",
                            value: companyData.CompFax,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        */}
                        {renderFieldRow([
                          {
                            label: "Currency",
                            name: "CompCurrency",
                            value: companyData.CompCurrency,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Time Zone",
                            name: "CompTimeZone",
                            value: companyData.CompTimeZone,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        {/* Company Slug moved to profile header panel
                        {renderFieldRow([
                          {
                            label: "Company Slug",
                            name: "CompanySlug",
                            value: companyData.CompanySlug,
                            isEditable: false,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        */}
                        <div className="list-group-item" style={{ borderBottom: "none", paddingTop: "10px", paddingBottom: "10px" }}>
                          <div className="row g-2">
                            {renderField(
                            "Address",
                            "CompAddress",
                            companyData.CompAddress,
                            true,
                            true,
                            true,
                            false,
                            "col-12",
                            true
                          )}
                          </div>
                        </div>
                        <div
                          className="list-group-item py-2 px-3 d-flex justify-content-end align-items-center profile-action-row"
                          style={{ columnGap: "8px", borderBottom: "none" }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                            onClick={cancelGeneralEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times" style={{ fontSize: "10px" }}></i>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--primary text-white d-inline-flex align-items-center gap-2"
                            onClick={saveGeneralSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save" style={{ fontSize: "10px" }}></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>
                </section>
                <section
                  id="socialLinks"
                  className="card border-0 mb-3 profile-section"
                 
                >
                  <div className="card-body">
                    {renderSectionHeading(
                      "Social Links",
                      "Manage your company's social media and website links."
                    )}
                  <div className="mb-0">
                    {!isSocialLinksEditing ? (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: 'none' }}>
                            {renderFieldRow([
                          {
                            label: "Website",
                            name: "CompWebsite",
                            value: companyData.CompWebsite,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Facebook Link",
                            name: "CompFacebooklink",
                            value: companyData.CompFacebooklink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Instagram Link",
                            name: "CompInstagramLink",
                            value: companyData.CompInstagramLink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "YouTube Link",
                            name: "CompYoutubeLink",
                            value: companyData.CompYoutubeLink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "TikTok Link",
                            name: "CompTikTokLink",
                            value: companyData.CompTikTokLink,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Google Review Link",
                            name: "CompGoogleReview",
                            value: companyData.CompGoogleReview,
                            isEditable: true,
                            sectionEditing: isSocialLinksEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        <div className="list-group-item profile-action-row" style={{ borderBottom: 'none', paddingTop: '10px', paddingBottom: '12px' }}>
                          <div className="d-flex justify-content-end">
                            <button
                              type="button"
                              className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                              onClick={startSocialLinksEditing}
                            >
                              <i className="fas fa-pen" style={{ fontSize: "10px" }} />
                              Edit
                            </button>
                          </div>
                        </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: "none" }}>
                        {renderFieldRow([
                          {
                            label: "Website",
                            name: "CompWebsite",
                            value: companyData.CompWebsite,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Facebook Link",
                            name: "CompFacebooklink",
                            value: companyData.CompFacebooklink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Instagram Link",
                            name: "CompInstagramLink",
                            value: companyData.CompInstagramLink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "YouTube Link",
                            name: "CompYoutubeLink",
                            value: companyData.CompYoutubeLink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "TikTok Link",
                            name: "CompTikTokLink",
                            value: companyData.CompTikTokLink,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Google Review Link",
                            name: "CompGoogleReview",
                            value: companyData.CompGoogleReview,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        <div
                          className="list-group-item py-2 px-3 d-flex justify-content-end align-items-center profile-action-row"
                          style={{ columnGap: "8px", borderBottom: "none" }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                            onClick={cancelSocialLinksEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times" style={{ fontSize: "10px" }}></i>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--primary text-white d-inline-flex align-items-center gap-2"
                            onClick={saveSocialLinksSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save" style={{ fontSize: "10px" }}></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>
                </section>
                <section
                  id="businessDetails"
                  className="card border-0 mb-3 profile-section"
                 
                >
                  <div className="card-body">
                    {renderSectionHeading(
                      "Business Details",
                      "Business email, binding code, and company business settings."
                    )}
                  <div className="mb-0">
                    {!isBusinessDetailsEditing ? (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: 'none' }}>
                            <div
                              className="list-group-item"
                              style={{ borderBottom: "none", paddingTop: "12px", paddingBottom: "4px" }}
                            >
                              <p className="profile-subsection-title mb-1">Email &amp; binding</p>
                              <p className="profile-subsection-hint mb-2">
                                Business email uses a confirmation link. Binding code is edited with the section below.
                              </p>
                              {renderCompanyEmailBanner()}
                            </div>
                            {renderFieldRow([
                          {
                            label: "Business Type",
                            name: "CompIndustryType",
                            value: companyData.CompIndustryType,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Binding Code",
                            name: "CompSecCode",
                            value: companyData.CompSecCode,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Latitude",
                            name: "CompLatitude",
                            value: companyData.CompLatitude,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                          {
                            label: "Longitude",
                            name: "CompLongitude",
                            value: companyData.CompLongitude,
                            isEditable: true,
                            sectionEditing: isBusinessDetailsEditing,
                            hideIndividualEdit: true,
                          },
                        ], { compact: true, dense: true })}
                        <div className="list-group-item profile-action-row" style={{ borderBottom: 'none', paddingTop: '10px', paddingBottom: '12px' }}>
                          <div className="d-flex justify-content-end">
                            <button
                              type="button"
                              className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                              onClick={startBusinessDetailsEditing}
                            >
                              <i className="fas fa-pen" style={{ fontSize: "10px" }} />
                              Edit
                            </button>
                          </div>
                        </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: "none" }}>
                            <div
                              className="list-group-item"
                              style={{ borderBottom: "none", paddingTop: "12px", paddingBottom: "4px" }}
                            >
                              <p className="profile-subsection-title mb-1">Email &amp; binding</p>
                              <p className="profile-subsection-hint mb-2">
                                Email changes still use the confirmation-link flow. Binding code saves with this section.
                              </p>
                              {renderCompanyEmailBanner()}
                            </div>
                        {renderFieldRow([
                          {
                            label: "Business Type",
                            name: "CompIndustryType",
                            value: companyData.CompIndustryType,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Binding Code",
                            name: "CompSecCode",
                            value: companyData.CompSecCode,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Latitude",
                            name: "CompLatitude",
                            value: companyData.CompLatitude,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Longitude",
                            name: "CompLongitude",
                            value: companyData.CompLongitude,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        <div
                          className="list-group-item py-2 px-3 d-flex justify-content-end align-items-center profile-action-row"
                          style={{ columnGap: "8px", borderBottom: "none" }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                            onClick={cancelBusinessDetailsEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times" style={{ fontSize: "10px" }}></i>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--primary text-white d-inline-flex align-items-center gap-2"
                            onClick={saveBusinessDetailsSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save" style={{ fontSize: "10px" }}></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>
                </section>
                <section
                  id="security"
                  className="card border-0 mb-3 profile-section"
                 
                >
                  <div className="card-body">
                    {renderSectionHeading(
                      "Security & contacts",
                      "Account recovery contacts and receipt contact details."
                    )}
                  <div className="mb-0">
                    {!isSecurityEditing ? (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: 'none' }}>
                            <div className="list-group-item" style={{ borderBottom: "none", paddingBottom: "4px", paddingTop: "10px" }}>
                              <p className="profile-subsection-title mb-0">Security contacts</p>
                              <p className="profile-subsection-hint mb-0">
                                Used for account access, recovery, and security alerts.
                              </p>
                            </div>
                            {renderFieldRow([
                              {
                                label: "Security Contact Email",
                                name: "CompRecoveryEmail",
                                value: companyData.CompRecoveryEmail,
                                isEditable: true,
                                sectionEditing: isSecurityEditing,
                                hideIndividualEdit: true,
                              },
                              {
                                label: "Security Contact Phone",
                                name: "CompRecoveryPhoneNumber",
                                value: companyData.CompRecoveryPhoneNumber,
                                isEditable: true,
                                sectionEditing: isSecurityEditing,
                                hideIndividualEdit: true,
                              },
                            ], { compact: true, dense: true })}
                            <div className="list-group-item" style={{ borderBottom: "none", paddingBottom: "4px", paddingTop: "14px" }}>
                              <p className="profile-subsection-title mb-0">Receipt contacts</p>
                              <p className="profile-subsection-hint mb-0">
                                These will be printed on the receipt and shown in online ordering.
                              </p>
                            </div>
                            {renderFieldRow([
                              {
                                label: "Contact Email",
                                name: "S_OnlineOrderingEmail",
                                value: companyData.S_OnlineOrderingEmail,
                                isEditable: true,
                                sectionEditing: isSecurityEditing,
                                hideIndividualEdit: true,
                              },
                              {
                                label: "Contact Phone Number",
                                name: "S_OnlineOrderingPhone",
                                value: companyData.S_OnlineOrderingPhone,
                                isEditable: true,
                                sectionEditing: isSecurityEditing,
                                hideIndividualEdit: true,
                              },
                            ], { compact: true, dense: true })}
                            <div className="list-group-item profile-action-row" style={{ borderBottom: 'none', paddingTop: '10px', paddingBottom: '12px' }}>
                              <div className="d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                                  onClick={startSecurityEditing}
                                >
                                  <i
                                    className={`fas ${
                                      companyData.CompRecoveryEmail ||
                                      companyData.CompRecoveryPhoneNumber ||
                                      companyData.S_OnlineOrderingEmail ||
                                      companyData.S_OnlineOrderingPhone
                                        ? "fa-pen"
                                        : "fa-plus"
                                    }`}
                                    style={{ fontSize: "10px" }}
                                  />
                                  {companyData.CompRecoveryEmail ||
                                  companyData.CompRecoveryPhoneNumber ||
                                  companyData.S_OnlineOrderingEmail ||
                                  companyData.S_OnlineOrderingPhone
                                    ? "Edit"
                                    : "Add"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="card profile-inner-panel">
                          <div className="list-group list-group-flush" style={{ border: "none" }}>
                        <div className="list-group-item" style={{ borderBottom: "none", paddingBottom: "4px", paddingTop: "10px" }}>
                          <p className="profile-subsection-title mb-0">Security contacts</p>
                          <p className="profile-subsection-hint mb-0">
                            Used for account access, recovery, and security alerts.
                          </p>
                        </div>
                        {renderFieldRow([
                          {
                            label: "Security Contact Email",
                            name: "CompRecoveryEmail",
                            value: companyData.CompRecoveryEmail,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Security Contact Phone",
                            name: "CompRecoveryPhoneNumber",
                            value: companyData.CompRecoveryPhoneNumber,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        <div className="list-group-item" style={{ borderBottom: "none", paddingBottom: "4px", paddingTop: "14px" }}>
                          <p className="profile-subsection-title mb-0">Receipt contacts</p>
                          <p className="profile-subsection-hint mb-0">
                            These will be printed on the receipt and shown in online ordering.
                          </p>
                        </div>
                        {renderFieldRow([
                          {
                            label: "Contact Email",
                            name: "S_OnlineOrderingEmail",
                            value: companyData.S_OnlineOrderingEmail,
                            isEditable: true,
                            sectionEditing: true,
                          },
                          {
                            label: "Contact Phone Number",
                            name: "S_OnlineOrderingPhone",
                            value: companyData.S_OnlineOrderingPhone,
                            isEditable: true,
                            sectionEditing: true,
                          },
                        ], { compact: true, dense: true })}
                        <div
                          className="list-group-item py-2 px-3 d-flex justify-content-end align-items-center profile-action-row"
                          style={{ columnGap: "8px", borderBottom: "none" }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--ghost d-inline-flex align-items-center gap-2"
                            onClick={cancelSecurityEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times" style={{ fontSize: "10px" }}></i>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm profile-btn profile-btn--primary text-white d-inline-flex align-items-center gap-2"
                            onClick={saveSecuritySettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i
                                  className={`fas ${
                                    (securityPreviousData?.CompRecoveryEmail || "").trim() ||
                                    (securityPreviousData?.CompRecoveryPhoneNumber || "").trim() ||
                                    (securityPreviousData?.S_OnlineOrderingEmail || "").trim() ||
                                    (securityPreviousData?.S_OnlineOrderingPhone || "").trim()
                                      ? "fa-save"
                                      : "fa-plus"
                                  }`}
                                  style={{ fontSize: "10px" }}
                                ></i>
                                {(securityPreviousData?.CompRecoveryEmail || "").trim() ||
                                (securityPreviousData?.CompRecoveryPhoneNumber || "").trim() ||
                                (securityPreviousData?.S_OnlineOrderingEmail || "").trim() ||
                                (securityPreviousData?.S_OnlineOrderingPhone || "").trim()
                                  ? "Update"
                                  : "Add"}
                              </>
                            )}
                          </button>
                        </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>
                </section>
                {/* Licenses moved into profile header summary
                <section
                  id="licenses"
                  className="card border-0 mb-3 profile-section"
                 
                >
                  <div className="card-body">
                    {renderSectionHeading(
                      "Licenses",
                      "View your company licenses."
                    )}
                  <div className="mb-0">
                    <div className="card profile-inner-panel">
                      <div className="list-group list-group-flush" style={{ border: 'none' }}>
                        {renderFieldRow([
                          {
                            label: "Admin SMS credits (wallet grant)",
                            name: "CompMonthlySmsLimit",
                            value: companyData.CompMonthlySmsLimit,
                            isEditable: false,
                          },
                          {
                            label: "Max User License",
                            name: "CompMaxUserLicense",
                            value: companyData.CompMaxUserLicense,
                            isEditable: false,
                          },
                        ], { compact: true, dense: true })}
                        {renderFieldRow([
                          {
                            label: "Registration Start Date",
                            name: "CompRegDate",
                            value: companyData.CompRegDate,
                            isEditable: false,
                          },
                          {
                            label: "Registration End Date",
                            name: "CompExpireDate",
                            value: companyData.CompExpireDate,
                            isEditable: false,
                          },
                        ], { compact: true, dense: true })}
                      </div>
                    </div>
                  </div>
                  </div>
                </section>
                */}
              </>
            ) : (
              <div
                className={`border-l-4 p-4 rounded-lg ${
                  isDarkMode
                    ? "bg-yellow-900 border-yellow-600"
                    : "bg-yellow-50 border-yellow-400"
                }`}
                style={{
                  backgroundColor: isDarkMode ? "#451a03" : "#fefce8",
                  borderColor: isDarkMode ? "#d97706" : "#facc15",
                }}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <i
                      className={`fas fa-exclamation-triangle ${
                        isDarkMode ? "text-yellow-400" : "text-yellow-400"
                      }`}
                      style={{ color: isDarkMode ? "#fbbf24" : "#facc15" }}
                    ></i>
                  </div>
                  <div className="ml-3">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-yellow-200" : "text-yellow-700"
                      }`}
                      style={{ color: isDarkMode ? "#fde68a" : "#a16207" }}
                    >
                      No company data available. Please try refreshing the page
                      or contact support.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </GridSectionLoader>
      </div>
    </>
  );
}
export default Profile;